import {
  Anchor,
  Box,
  Button,
  Card,
  Center,
  CloseButton,
  Combobox,
  Grid,
  Input,
  InputBase,
  MultiSelect,
  Pagination,
  ScrollArea,
  Select,
  Table,
  Text,
  useCombobox,
} from '@mantine/core';
import { IconExternalLink, IconSearch } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import eventTypes from '../../constants/eventTypes';
import formatDate from '../../constants/formattedDate';
import { dateToEndOfDay, dateToStartOfDay, daysAgoISODate, todayISODate } from '../../helpers/dateUtils';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import MetricsSelector from '../MetricsSelector';
import EventIcon from '../Profiles/EventIcon';
import Spinner from '../Spinner';

const DOMAIN_SEARCH_DEBOUNCE_MS = 300;

const domainLabel = (d) => `${d.accountName} — ${d.name}`;

const SuperAdminOperationalEmailEvents = () => {
  const fetch = useFetch();
  const [kinds, setKinds] = useState();
  const [actions, setActions] = useState([]);
  const [kind, setKind] = useState();
  const [eventFilter, setEventFilter] = useState([]);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState(daysAgoISODate(29));
  const [endDate, setEndDate] = useState(todayISODate());
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainSearch, setDomainSearch] = useState('');
  const [domainResults, setDomainResults] = useState([]);
  const [result, setResult] = useState();
  const [loadError, setLoadError] = useState();
  const [retryNonce, setRetryNonce] = useState(0);

  const domainCombobox = useCombobox({
    onDropdownClose: () => {
      domainCombobox.resetSelectedOption();
      setDomainSearch('');
    },
    onDropdownOpen: () => {
      domainCombobox.focusSearchInput();
    },
  });

  useEffect(() => {
    fetch('/api/admin/operational-emails/kinds')
      .then(({ kinds: ks, actions: acts }) => {
        setKinds(ks);
        setActions(acts || []);
        if (ks?.length && !kind) setKind(ks[0].key);
      })
      .catch(setLoadError);
    // Retry re-runs this too: when the kinds call is the one that failed, no kind is
    // selected and the events effect below bails out, so retrying it alone is a no-op.
    // kind is read defensively (only set if unset), so it stays out of the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryNonce]);

  // Filter changes reset to page 1 in the same render via these wrapper setters,
  // so no separate page-reset effect is needed.
  const changeKind = (v) => {
    setPage(1);
    setKind(v);
  };
  const changeEventFilter = (v) => {
    setPage(1);
    setEventFilter(v);
  };
  const changeStartDate = (v) => {
    setPage(1);
    setStartDate(v);
  };
  const changeEndDate = (v) => {
    setPage(1);
    setEndDate(v);
  };
  const changeDomain = (v) => {
    setPage(1);
    setSelectedDomain(v);
  };

  // Search-as-you-type over every domain on the platform. Debounced so a burst of
  // keystrokes issues one request; the cleanup both cancels the pending timer and
  // guards against an in-flight response for a stale query landing last.
  useEffect(() => {
    const q = domainSearch.trim();
    if (!q) {
      setDomainResults([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      fetch(`/api/admin/operational-emails/domains-search?q=${encodeURIComponent(q)}`)
        .then(({ domains }) => {
          if (!cancelled) setDomainResults(domains || []);
        })
        .catch(() => {
          // A failed lookup just leaves the dropdown empty; the events table below
          // keeps its own error surface for the calls that actually carry data.
          if (!cancelled) setDomainResults([]);
        });
    }, DOMAIN_SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [domainSearch, fetch]);

  const domainId = selectedDomain?.id ?? null;

  // Single effect with cancellation so a stale fetch can't overwrite a fresh one.
  useEffect(() => {
    if (!kind) return undefined;
    let cancelled = false;
    setResult(undefined);
    setLoadError(undefined);

    const params = new URLSearchParams({
      from: dateToStartOfDay(startDate, 'local'),
      to: dateToEndOfDay(endDate, 'local'),
      kind,
      page: String(page),
    });
    for (const key of eventFilter) params.append('eventFilter[]', key);
    if (domainId) params.set('domainId', String(domainId));

    fetch(`/api/admin/operational-emails/events?${params.toString()}`)
      .then((r) => {
        if (!cancelled) setResult(r);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e);
      });

    return () => {
      cancelled = true;
    };
  }, [kind, startDate, endDate, eventFilter, domainId, page, retryNonce, fetch]);

  const events = result?.events || [];

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="md">
        <Grid align="flex-end" gutter="md">
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Select
              label="Kind"
              value={kind || null}
              onChange={changeKind}
              disabled={!kinds}
              data={(kinds || []).map((k) => ({ value: k.key, label: k.displayName }))}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <MultiSelect
              label="Event type"
              placeholder="All events"
              value={eventFilter}
              onChange={changeEventFilter}
              clearable
              data={actions.map((a) => ({ value: a.key, label: a.label }))}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Combobox
              store={domainCombobox}
              onOptionSubmit={(value) => {
                const picked = domainResults.find((d) => d.id.toString() === value);
                if (picked) changeDomain(picked);
                domainCombobox.closeDropdown();
              }}
            >
              <Combobox.Target>
                <InputBase
                  label="Account / Domain"
                  component="button"
                  type="button"
                  pointer
                  onClick={() => domainCombobox.toggleDropdown()}
                  rightSection={
                    selectedDomain ? (
                      <CloseButton
                        size="sm"
                        aria-label="Clear domain filter"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => changeDomain(null)}
                      />
                    ) : (
                      <Combobox.Chevron />
                    )
                  }
                  rightSectionPointerEvents={selectedDomain ? 'all' : 'none'}
                >
                  {selectedDomain ? domainLabel(selectedDomain) : <Input.Placeholder>All domains</Input.Placeholder>}
                </InputBase>
              </Combobox.Target>

              <Combobox.Dropdown>
                <Combobox.Search
                  value={domainSearch}
                  onChange={(event) => setDomainSearch(event.currentTarget.value)}
                  placeholder="Search accounts and domains..."
                  leftSection={<IconSearch size={14} />}
                />
                <Combobox.Options>
                  <ScrollArea.Autosize type="auto" mah={300}>
                    {domainResults.length ? (
                      domainResults.map((d) => (
                        <Combobox.Option value={d.id.toString()} key={d.id}>
                          {domainLabel(d)}
                        </Combobox.Option>
                      ))
                    ) : (
                      <Combobox.Empty>No domains found</Combobox.Empty>
                    )}
                  </ScrollArea.Autosize>
                </Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 3 }}>
            <MetricsSelector
              startDate={startDate}
              endDate={endDate}
              setStartDate={changeStartDate}
              setEndDate={changeEndDate}
            />
          </Grid.Col>
        </Grid>

        {loadError ? (
          <Box mt="md">
            <ErrorMessage message={loadError.message || 'Failed to load events'} />
            <Center mt="sm">
              <Button variant="light" onClick={() => setRetryNonce((n) => n + 1)}>
                Retry
              </Button>
            </Center>
          </Box>
        ) : null}

        {kinds && kinds.length === 0 ? (
          <Center py="xl">
            <Text c="dimmed">No operational email kinds configured.</Text>
          </Center>
        ) : !result && !loadError ? (
          <Spinner />
        ) : !result ? null : events.length ? (
          <>
            <Table highlightOnHover mt="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Time</Table.Th>
                  <Table.Th>Recipient</Table.Th>
                  <Table.Th>Details</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {events.map((event, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <EventIcon eventName={event.action} />{' '}
                      {eventTypes.find((t) => t.value === event.action)?.label || event.action}
                    </Table.Td>
                    <Table.Td>{formatDate(event.timestamp)}</Table.Td>
                    <Table.Td>{event.recipientEmail}</Table.Td>
                    <Table.Td>
                      {event.action === 'emailSend' && event.url ? (
                        <Anchor href={event.url} target="_blank" rel="noreferrer noopener">
                          View in browser <IconExternalLink size={14} />
                        </Anchor>
                      ) : null}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {result.pagesCount > 1 ? (
              <Center mt="md">
                <Pagination total={result.pagesCount} value={page} onChange={setPage} />
              </Center>
            ) : null}
          </>
        ) : (
          <Center py="xl">
            <Text c="dimmed">No events matching the current filters.</Text>
          </Center>
        )}
      </Card.Section>
    </Card>
  );
};

export default SuperAdminOperationalEmailEvents;
